/**
 * Crews (plural) — which ones I belong to, which one I am looking at, and the
 * switch between them. docs/CREW-ONBOARDING.md D2 and D10.
 *
 * Distinct from `slices/crew.ts`, which is the ROSTER of the crew I am in.
 * This one is the layer above: the set of crews, and moving between them.
 *
 * Nothing renders any of this until phase D builds the switcher. Phase C's job
 * is that the state and the transition exist and are correct.
 */

import type { StateCreator } from 'zustand';

import type { CreateCrewInput, CrewIdentity, CrewMembership } from '../../repo/Repository.ts';
import { RepoError } from '../../repo/Repository.ts';
import { emptyBoardQueries } from '../filterKey.ts';
import { whenIdle } from '../mutate.ts';
import type { QdStore } from '../types.ts';
import { SCHEDULE_CLEARED } from './schedule.ts';

export interface CrewsSlice {
  /** Every crew I belong to, name-ordered. Empty is the no-crew state. */
  crews: CrewMembership[];
  /**
   * The crew the LOADED DATA actually came from, straight off the snapshot —
   * not the crew we asked for. The server resolves which crew a read returns,
   * so these can differ, and the difference is the only signal that something
   * moved underneath a live session (D2).
   */
  activeCrew: CrewIdentity | null;
  /**
   * Which crew-lifecycle write is open right now, `''` when none.
   *
   * ONE flag for the whole lane, deliberately. Switching, starting, handing over
   * and deleting all repoint or destroy the active crew, so a second one
   * starting mid-flight is a race and not a queue — mid-delete the doomed crew
   * is still in `crews` while `activeCrew` is already null, so `switchCrew`'s
   * own "am I already there" test passes and two `hydrateAfterAuth()` calls and
   * two `nav()`s interleave, last one home winning.
   *
   * It replaces the old `switching`, whose comment claimed the switcher disabled
   * itself on it. It did not: the flag reached `switcherVM` and no component
   * ever read it. Every surface in this lane now reads this one.
   */
  crewBusy: '' | 'switch' | 'create' | 'transfer' | 'delete' | 'rename';

  loadCrews: () => Promise<void>;
  switchCrew: (crewId: string) => Promise<void>;
  /**
   * Start a crew. With `account` it is the signed-OUT wizard, which creates the
   * account in the same transaction and signs in as it; without, it is
   * `+ START ANOTHER CREW` from inside the app.
   */
  startCrew: (input: CreateCrewInput) => Promise<CrewMembership | null>;
  removeCrew: (crewId: string, confirmName: string) => Promise<boolean>;
  /** Hand the active crew to another member. Owner only, irreversible. */
  handOver: (userKey: string) => Promise<boolean>;
  /** The CREW NAME box on MANAGE CREW, owner-only (3030). */
  crewNameDraft: string;
  setCrewNameDraft: (v: string) => void;
  renameCrew: () => Promise<boolean>;
}

/**
 * Everything scoped to one crew, blanked on the way out.
 *
 * Written as an explicit list rather than a rebuild-from-scratch because the
 * store also holds things that MUST survive a switch — the catalog, the Steam
 * link, session and UI state — and a "reset everything except…" list is the
 * one that rots silently when a field is added.
 *
 * The boards maps are keyed by user, not by crew, so they are cleared whole:
 * my own board differs per crew, and a stale one would render the previous
 * crew's tiers under the new crew's name until the refetch lands.
 */
export function clearedCrewState(): Partial<QdStore> {
  return {
    users: [],
    list: [],
    pts: [],
    videos: [],
    // Steam ownership and link state are crew-scoped reads (3017/D9), so they
    // describe the crew we are leaving.
    crewOwns: {},
    // `crew_games_ownership` answers about the CALLER'S crew, so carrying this
    // across a switch would filter the new crew's boards by the old crew's
    // libraries — and silently, since the answers look identical in shape.
    ownBy: {},
    crewLinks: null,
    potentials: {},
    tiers: {},
    tierOrder: {},
    skips: {},
    intOrder: {},
    // Crew-scoped like `intOrder` (both live in `interest_order`, which
    // `fetchMyBoards` reads with an `.eq('crew_id', …)`), so a stale one would
    // order the NEW crew's TO-DO panel by the old crew's deferrals.
    intSkips: {},
    /* Crewmates' interest orderings (3059) — crew-scoped twice over: the RPC
       answers for `my_crew_id()`, and `interest_order` rows carry a `crew_id`
       because since 3016 one account ranks the same game differently in two
       crews. Carried across a switch, Games Tonight filtered to one member
       would sort the NEW crew's tiers by the ordering they set in the OLD one
       — which looks like a working feature and is a fabrication. */
    crewIntOrder: {},
    /* The party's scores (3060) are a crew-scoped READ: the policy answers
       for the active crew's members, so the map describes the party being
       left. The backlog stays — it is yours in every party. */
    scores: {},
    /* Recommendations (3062) are crew-scoped outright — made in a party,
       between its members. The next party's are fetched with its snapshot. */
    sends: [],
    /* The board's PARTY rows (3063) are read for the active crew; the WORLD
       rows are yours and come back with the next read. The watch stays — it
       is yours in every party, like the shelf. */
    board: [],
    /* THE CATALOG IS CREW-SHAPED TOO, and leaving it out of this blank put a
       half-empty board in front of Marcus (production, 2026-08-22).
       `games_bootstrap` returns the global top-200 head PLUS the CALLER'S
       needed set — their crew's list, their rankings — so a catalog fetched in
       one context cannot join another context's list. `hydrateAfterAuth` only
       refetches when the catalog is EMPTY (the cold-boot dedupe, P4b-01), so
       every transition through here must clear it or the guard skips the
       refetch and `entriesOf` silently drops the rows that cannot join. */
    catalog: [],
    // The round is crew-scoped too (3039): the chip and the join card read it,
    // and the previous crew's round pulsing under the new crew's name would be
    // an invitation to a session you are not in.
    round: null,
    rArm: '',
    rSheet: false,
    // SCHEDULE (3071): the party's calendar, and every piece of state that
    // pointed into it — a night page, an open game modal — goes with it.
    ...SCHEDULE_CLEARED,
    /* Board searches are crew-scoped by what they narrow: every pool behind
       them (`list`, `potentials`, the round's shelf) is blanked above, so a
       term typed against the old crew would meet the new crew's rows and read
       `0 OF 12 ON THE LIST` about a question nobody asked here (2026-08-28). */
    bqBy: emptyBoardQueries(),
  };
}

export const createCrewsSlice: StateCreator<QdStore, [], [], CrewsSlice> = (set, get) => ({
  crews: [],
  activeCrew: null,
  crewBusy: '',

  loadCrews: async () => {
    try {
      set({ crews: await get().repo.myCrews() });
    } catch {
      // A failed crew list is not worth a toast: every surface that uses it is
      // phase D's, and the crew you are IN still works. Leaving the previous
      // list in place beats blanking the switcher on one bad fetch.
    }
  },

  /**
   * D10: drain → repoint → clear → re-hydrate → land on Tonight.
   *
   * THE DRAIN IS FIRST AND IT IS NOT OPTIONAL. The board RPCs resolve
   * `crew_id := my_crew_id()` on the server, inside their own bodies, so a
   * commit that is still open when the active crew moves writes the OLD crew's
   * action into the NEW one — and reports success. Worst case that costs one
   * retry window of waiting, which is the price of not silently misfiling
   * somebody's vote.
   */
  switchCrew: async (crewId) => {
    const s = get();
    if (s.crewBusy || crewId === s.activeCrew?.id) return;
    set({ crewBusy: 'switch' });
    try {
      await whenIdle();
      await s.repo.setActiveCrew(crewId);

      // Blank before refetching, not after: the header and every list read
      // from the store, and leaving the old crew's rows up during the fetch
      // shows the previous crew's board under the new crew's name.
      set({ ...clearedCrewState(), activeCrew: null });

      // The same path a cold sign-in takes — refetch, re-subscribe, and the
      // snapshot brings back the crew identity, which is what finally sets
      // `activeCrew`.
      await get().hydrateAfterAuth();
      await get().loadCrews();
      // A party switch lands on MY GAMES like a sign-in does (2026-09-03).
      get().nav('mine');
    } catch {
      get().toast("✕ COULDN'T SWITCH PARTIES — TRY AGAIN", true);
      // Re-hydrate regardless: the server-side switch may well have landed
      // before whatever failed, and leaving the store blanked would show an
      // empty crew that is not empty.
      await get().hydrateAfterAuth().catch(() => undefined);
    } finally {
      set({ crewBusy: '' });
    }
  },

  startCrew: async (input) => {
    if (get().crewBusy) return null;
    set({ crewBusy: 'create' });
    try {
      await whenIdle();
      const crew = await get().repo.createCrew(input);
      // crew-create already switched the server-side active crew inside its
      // transaction, so this is the second half of a switch, not a new one.
      set({ ...clearedCrewState(), activeCrew: null });
      await get().hydrateAfterAuth();
      await get().loadCrews();
      get().nav('mine');
      return crew;
    } catch {
      get().toast("✕ COULDN'T START THAT PARTY — TRY AGAIN", true);
      return null;
    } finally {
      set({ crewBusy: '' });
    }
  },

  /**
   * TRANSFER OWNERSHIP (D4).
   *
   * IRREVERSIBLE FROM THIS SIDE: the moment it lands you are a player, so you
   * cannot take it back — only the new owner can hand it on. The surface arms
   * before it calls, and the toast says what happened rather than that it
   * succeeded.
   *
   * Re-hydrates rather than patching `users` locally: the swap moves two
   * membership rows and the legacy role column on both accounts, and the
   * admin panel unmounts underneath you the moment `meAdmin` flips. Refetching
   * is the only version of that which cannot leave a half-updated roster.
   */
  crewNameDraft: '',
  setCrewNameDraft: (crewNameDraft) => set({ crewNameDraft }),

  /**
   * Rename the ACTIVE crew (3030). Owner only, and the server checks it again.
   *
   * Not optimistic, unlike most writes in this app. The crew's name is on the
   * header, the switcher and the panel at once, so a rollback would be three
   * things flickering back — and this is a deliberate, low-frequency act where
   * a moment of RENAMING… costs nothing. `refreshSnapshot` is what republishes
   * the new name to all three.
   */
  renameCrew: async () => {
    const s = get();
    const crew = s.activeCrew;
    if (s.crewBusy || !crew) return false;
    const next = s.crewNameDraft.trim();
    if (!next) {
      s.toast('✕ PARTY NAME REQUIRED', true);
      return false;
    }
    // Nothing to do, and saying so beats a round trip that reports success for
    // a change nobody made.
    if (next === crew.name) return true;

    set({ crewBusy: 'rename' });
    try {
      await whenIdle();
      const stored = await s.repo.renameCrew(crew.id, next);
      await get().refreshSnapshot();
      await get().loadCrews();
      // The STORED name, so a trimmed edge case leaves the box agreeing with
      // the row rather than with what was typed.
      set({ crewNameDraft: stored });
      get().toast('PARTY RENAMED — ' + stored.toUpperCase());
      return true;
    } catch (e) {
      // 3030's refusals are written to be read: "crew name is required",
      // "crew name is too long", "not the owner of that crew".
      const msg = e instanceof RepoError && e.message ? e.message.toUpperCase() : '';
      get().toast(msg || "✕ COULDN'T RENAME THE PARTY — TRY AGAIN", true);
      return false;
    } finally {
      set({ crewBusy: '' });
    }
  },

  handOver: async (userKey) => {
    if (get().crewBusy) return false;
    set({ crewBusy: 'transfer' });
    try {
      await whenIdle();
      await get().repo.transferOwnership(userKey);
      await get().refreshSnapshot();
      await get().loadCrews();
      // Closing HERE, not at the click, is what lets the dialog carry the
      // failure below: the panel is still on screen to receive it.
      get().closeOwn();
      get().toast('HANDED OVER — YOU ARE NOW A PLAYER');
      return true;
    } catch (e) {
      // 3020's refusals are written to be read: "only the crew owner…",
      // "that member has not finished their first sign-in yet".
      const msg = e instanceof RepoError && e.message ? e.message.toUpperCase() : '';
      get().toast(msg || "✕ COULDN'T HAND THE PARTY OVER — TRY AGAIN", true);
      return false;
    } finally {
      set({ crewBusy: '' });
    }
  },

  removeCrew: async (crewId, confirmName) => {
    if (get().crewBusy) return false;

    // Read before the delete: the party is gone by the time we toast, and a
    // toast that said "PARTY DISBANDED" would not tell you WHICH, which matters
    // when you lead more than one.
    const name = get().crews.find((c) => c.crewId === crewId)?.name ?? '';
    const nameUp = name.toUpperCase();

    // THE TWO GATES ARE NOT THE SAME COMPARISON, and this is where they meet.
    //
    // The prompt asks for the name in CAPS because every label on these screens
    // is in caps, and the design mirror accepts what it asked for — it compares
    // case-insensitively. `crew-delete` and 3022 do NOT: they demand the stored
    // name byte for byte, so an owner of `The Crew` who typed back the
    // `THE CREW` we showed them would be refused by the server for doing as
    // they were told.
    //
    // So the typed value is checked HERE, in the shape the design specifies,
    // and what travels is the canonical name. The server's exact match still
    // does its real job: it is a stale-belief check, and a rename between
    // opening this dialog and confirming it makes the name we send wrong — and
    // refused — which is the window 3022's header exists to close.
    if (!name || confirmName.trim().toUpperCase() !== nameUp) {
      get().toast('✕ THAT IS NOT THE PARTY NAME', true);
      return false;
    }

    set({ crewBusy: 'delete' });
    try {
      await whenIdle();
      await get().repo.deleteCrew(crewId, name);
      // The dialog has been on screen saying DELETING… since the click; it goes
      // now that there is something to say instead.
      get().closeOwn();
      set({ ...clearedCrewState(), activeCrew: null });
      // Which crew the account lands on next is `my_crew_id()`'s answer, not
      // ours to guess — so re-read rather than picking one.
      await get().loadCrews();
      await get().hydrateAfterAuth();
      // Deleting your LAST crew leaves the account with nowhere to be, and
      // Tonight would render an empty board as though the crew were quiet.
      //
      // THE SIGNAL IS `crews`, NOT `activeCrew`, and the difference is a race
      // that cost Marcus a crew he still belonged to (staging, 2026-08-20).
      // `activeCrew` is the null WE set nine lines up; `hydrateAfterAuth` is
      // supposed to put it back, but the delete cascades our own `crew_members`
      // row, and the realtime channel is still bound to the crew we just
      // deleted — so the DELETE event lands, fires an unconditional
      // `refreshSnapshot`, and that later snapshot wins `boot.ts`'s epoch
      // guard. The one we awaited then resolves having written nothing, and it
      // returns `void`, so there is no way to tell that apart from "the server
      // says you have no crew". Reading `activeCrew` here is reading our own
      // null back and calling it an answer.
      //
      // `crews` is the awaited answer from `my_crews()` — a SECURITY DEFINER
      // membership read, not a snapshot, and nothing supersedes it. Filtering
      // the doomed crew out stays correct even when `loadCrews` failed and left
      // the pre-delete list standing, because it swallows its own errors.
      const remaining = get().crews.filter((c) => c.crewId !== crewId);
      get().nav(remaining.length ? 'mine' : 'landing');
      get().toast(nameUp ? nameUp + ' DISBANDED' : 'PARTY DISBANDED');
      return true;
    } catch {
      get().toast("✕ COULDN'T DISBAND THAT PARTY — TRY AGAIN", true);
      return false;
    } finally {
      set({ crewBusy: '' });
    }
  },
});
